import { useMemo, useState } from 'react';
import useCoupons from '../hooks/useCoupons';
import { Coupon, CouponType, CreateCouponInput, UpdateCouponInput } from '../types/coupon';
import { Plus, Percent, IndianRupeeIcon, Calendar, Trash2, Edit } from 'lucide-react';

const emptyForm: CreateCouponInput = {
  code: '',
  type: CouponType.PERCENTAGE,
  discount: 0,
  minOrderAmount: 0,
  expiresAt: new Date().toISOString().slice(0, 16),
};

const CouponsManagement = () => {
  const { coupons, isLoading, error, createCoupon, updateCoupon, deleteCoupon } = useCoupons();
  const [form, setForm] = useState<CreateCouponInput>({ ...emptyForm });
  const [editingId, setEditingId] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        discount: Number(form.discount),
        minOrderAmount: Number(form.minOrderAmount),
      } as CreateCouponInput;

      if (editingId) {
        await updateCoupon.mutateAsync({ id: editingId, payload: payload as UpdateCouponInput });
      } else {
        await createCoupon.mutateAsync(payload);
      }
      setForm({ ...emptyForm });
      setEditingId(null);
    } catch (err) {
      console.error(err);
    }
  };

  const startEdit = (coupon: Coupon) => {
    setEditingId(coupon.id);
    setForm({
      code: coupon.code,
      type: coupon.type,
      discount: coupon.discount,
      minOrderAmount: coupon.minOrderAmount,
      // convert ISO to input type datetime-local value
      expiresAt: new Date(coupon.expiresAt).toISOString().slice(0, 16),
    });
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this coupon?')) return;
    await deleteCoupon.mutateAsync(id);
    if (editingId === id) {
      setEditingId(null);
      setForm({ ...emptyForm });
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">{editingId ? 'Edit Coupon' : 'Create Coupon'}</h2>
        <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
            <input
              type="text"
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="SUMMER25"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={form.type}
              onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as CouponType }))}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={CouponType.PERCENTAGE}>Percentage</option>
              <option value={CouponType.FIXED}>Fixed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Discount</label>
            <input
              type="number"
              value={form.discount}
              onChange={(e) => setForm((f) => ({ ...f, discount: Number(e.target.value) }))}
              min={0}
              step={form.type === CouponType.PERCENTAGE ? 1 : 0.01}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={form.type === CouponType.PERCENTAGE ? 'e.g. 10 for 10%' : 'e.g. 100 for ₹100'}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Min Order Amount</label>
            <input
              type="number"
              value={form.minOrderAmount}
              onChange={(e) => setForm((f) => ({ ...f, minOrderAmount: Number(e.target.value) }))}
              min={0}
              step={0.01}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. 500"
              required
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Expires At</label>
            <input
              type="datetime-local"
              value={form.expiresAt}
              onChange={(e) => setForm((f) => ({ ...f, expiresAt: e.target.value }))}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="md:col-span-6 flex items-center gap-3">
            <button
              type="submit"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
              disabled={createCoupon.isPending || updateCoupon.isPending}
            >
              <Plus className="w-4 h-4" /> {editingId ? 'Update' : 'Create'}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={() => { setEditingId(null); setForm({ ...emptyForm }); }}
                className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold">All Coupons</h3>
        </div>
        <div className="divide-y divide-gray-100">
          {isLoading && <div className="p-4 text-sm text-gray-500">Loading...</div>}
          {error && <div className="p-4 text-sm text-red-600">Failed to load coupons</div>}
          {coupons?.length ? coupons.map((c) => (
            <div key={c.id} className="p-4 flex items-center justify-between">
              <div className="space-y-1">
                <div className="text-sm font-medium">{c.code}</div>
                <div className="text-xs text-gray-500 flex gap-3">
                  <span className="inline-flex items-center gap-1">
                    {c.type === CouponType.PERCENTAGE ? <Percent className="w-3 h-3" /> : <IndianRupeeIcon className="w-3 h-3" />}
                    {c.discount} {c.type === CouponType.PERCENTAGE ? '%' : ''}
                  </span>
                  <span className="inline-flex items-center gap-1">Min: ₹{c.minOrderAmount}</span>
                  <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> Expires: {new Date(c.expiresAt).toLocaleString()}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => startEdit(c)}
                  className="px-3 py-1.5 rounded-md border border-gray-300 hover:bg-gray-50 text-sm inline-flex items-center gap-1"
                >
                  <Edit className="w-4 h-4" /> Edit
                </button>
                <button
                  onClick={() => handleDelete(c.id)}
                  className="px-3 py-1.5 rounded-md border border-red-300 text-red-600 hover:bg-red-50 text-sm inline-flex items-center gap-1"
                >
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
            </div>
          )) : !isLoading && (
            <div className="p-6 text-center text-sm text-gray-500">No coupons found</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CouponsManagement;


